import { useContext, useEffect, useRef, useState } from "react";
import { CollectionFilterStateContext, FilterMap, SortOrder } from "../../../contexts/collection_filter_state";
import { DimensionsStateContext } from "../../../contexts/dimensions_state";
import { LayoutStateContext } from "../../../contexts/layout_state";
import { WindowSizeContext } from "../../../contexts/window_size";
import { useResizeObserver } from "../../../hooks/use_resize_observer";
import { useSmartState } from "../../../hooks/use_smart_state";
import { misc } from "../../../lib/misc";
import { prebuildData } from "../../../lib/prebuild-data";
import { FilterBarIconButton } from "./filter_bar/icon_button";
import { FilterBarTextButton } from "./filter_bar/text_button";

export type FilterBarProps = {
    width: number;
    open: boolean;
    bannerHeightReady: boolean;
    cancelControlsPointerEvents: boolean;
    showInline: boolean;
};

export function FilterBar({width,open,bannerHeightReady,cancelControlsPointerEvents,showInline}: FilterBarProps)
{
    open = true;

    const sortOrderCaptions = {
        "popular": "MOST \nPOPULAR",
        "reviewed": "BEST \nREVIEWED",
        "newest": "NEWEST",
        // "favorites": "MY \nFAVORITES",
    };
    // const filters = ["apple","cake","candy","ice","more"];
    const filters = [
        {id: "fruit",hint: "Fruit"},
        {id: "dessert",hint: "Dessert"},
        {id: "candy",hint: "Candy"},
        {id: "cool",hint: "Menthol / Agent Cool Recommended"},
        {id: "other",hint: "Others"}
    ];

    const { sortOrder, filter, isSwitching } = useContext(CollectionFilterStateContext);

    const [filterValue,setFilterValue] = useState<FilterMap>({});
    const filterValueRef = useRef(filterValue);
    filterValueRef.current = filterValue;

    const [sortOrderValue,setSortOrderValue] = useState<SortOrder>("popular");
    const sendFilterTimeoutRef = useRef(0);
    const sendSortOrderTimeoutRef = useRef(0);

    useEffect(() => 
    {
        if (sendFilterTimeoutRef.current!==0) clearTimeout(sendFilterTimeoutRef.current);
        sendFilterTimeoutRef.current = setTimeout(() => 
        {
            sendFilterTimeoutRef.current = 0;            
            filter.set(filterValue);
        },50) as unknown as number;
        
    },[filterValue]);

    useEffect(() => 
    {
        const { current: filterValue } = filterValueRef;
        if ((Object.keys(filter.value).length===0) && (Object.keys(filterValue).length>0))
        {
            setFilterValue({});
        }
    },[filter.value]);
    
    useEffect(() => 
    {
        if (sendSortOrderTimeoutRef.current!==0) clearTimeout(sendSortOrderTimeoutRef.current);
        sendSortOrderTimeoutRef.current = setTimeout(() => 
        {
            sendSortOrderTimeoutRef.current = 0;            
            sortOrder.set(sortOrderValue);
        },50) as unknown as number;
        
    },[sortOrderValue]);

    const selectedFilterIdRef = useRef(filterValue);
    selectedFilterIdRef.current = filterValue;
    const [controlsTop,setControlsTop] = useState(0);
    const [_showControls,setShowControls] = useState(false);

    const showControls = _showControls && bannerHeightReady;

    const { page,misc: { filterBarItemClassNameIdentifier },mobileDetect: { isActive: isMobile,widthCutoff: mobileCutoff } } = useContext(LayoutStateContext);
    const _windowSize = useContext(WindowSizeContext);
    const permRef = useRef({windowSize: _windowSize});
    permRef.current.windowSize = _windowSize;    

    const { 
        navbarHeight,
        bannerHeight,
        miniNavbarHeight,
        collectionGridHeight,
        filterBarControlsHeight,
        scrollBottomPadding
    } = useContext(DimensionsStateContext);

    const updateTop = () => 
    {
        const { windowSize } = permRef.current;
        const controlsHeight = filterBarControlsHeight.value;
        const scrollTop = page.scrollTop();
        const { height: windowHeight } = windowSize;
        const topSectionHeight = navbarHeight.value+bannerHeight.value;
        const maxAvailableSpace = windowHeight-miniNavbarHeight.value;
        const availableSpace = Math.min(windowHeight-topSectionHeight+scrollTop,maxAvailableSpace);
        scrollBottomPadding.set(Math.max(controlsHeight-collectionGridHeight.value,0));        

        if ((controlsHeight+10)<=availableSpace) {
            const stickyPosition = ((maxAvailableSpace-controlsHeight)/2)+miniNavbarHeight.value;
            setControlsTop(stickyPosition);
        }
        else {
            setControlsTop(miniNavbarHeight.value);
        }
        
        setShowControls(true);
    };

    const hideControls = useSmartState(false);

    useEffect(() => 
    {
        return page.addScrollEventListener(({}) => updateTop());
    },[]);

    useEffect(() => 
    {
        updateTop();
    },[_windowSize,navbarHeight.value,bannerHeight.value,miniNavbarHeight.value,collectionGridHeight.value,filterBarControlsHeight.value]);

    const paneClassNames = [filterBarItemClassNameIdentifier];

    const handlerFilterIconClicked = (id: string) =>
    {
        const updatedSelectedFilterId = {...selectedFilterIdRef.current};
        if (selectedFilterIdRef.current[id])
        {
            delete updatedSelectedFilterId[id];
        }
        else
        {
            updatedSelectedFilterId[id] = true;
        }
        setFilterValue(updatedSelectedFilterId);
    };

    const refOnResize = useResizeObserver(({height}) =>
    {
        filterBarControlsHeight.set(height);
    });

    useEffect(() => {
        if (isSwitching.value || page.isScrollLocked) {
            // const offsetTop = misc.getOffsetRelativeToParent(refOnResize.current,".filter-bar-back").top;
            // setFixedTop(controlsTop);
        }
        else {
            hideControls.value = false;
        }
    },[isSwitching.value || page.isScrollLocked]);

    const isBlocked = isSwitching.value || page.isScrollLocked;

    const { collectionFilterControls: { backgroundColor,color } } = prebuildData.getActiveTheme();

    return (
        <>
            <div className={"container slider"+(open?" open":"")} style={{width: `${width}rem`,transform: `translateX(${open?0:-width}rem)`}}>
            </div>
            <div ref={refOnResize} className={"controls slider"+(open?" open":"")+(showInline?" show-inline":"")+" "+paneClassNames.join(" ")+(hideControls.value?" hide-controls":"")} style={{
                    width: `${width}rem`,
                    transform: `translateX(${open?0:-width}rem)`,
                    ...(isMobile.current?{}:{
                        top: (controlsTop+(showControls?0:-100000)),
                    }),                    
                    pointerEvents: cancelControlsPointerEvents?"none":"all",
                }}>
                <div className="top-spacer"></div>
                {Object.keys(sortOrderCaptions).map((id: SortOrder) => <FilterBarTextButton key={id} disabled={id==="favorites"} paneClassNames={paneClassNames} text={sortOrderCaptions[id]} selected={sortOrderValue===id} onClick={() => setSortOrderValue(id)} /> )}
                <div className={["separator",paneClassNames].join(" ")}>
                    FLAVOR<br/>FILTER
                </div>
                {filters.map(({id,hint}) => <FilterBarIconButton key={id} paneClassNames={paneClassNames} name={id} selected={filterValue[id]?true:false} onClick={() => handlerFilterIconClicked(id)} hint={hint} />)}
                <div className="bottom-spacer"></div>
            </div>
            <style jsx>{`

            .slider.open {
                /* transition: transform 0.5s; */
            }

            .container {
                position: fixed;
                left: 0rem;
                top: 0rem;
                height: 100%;
                background-color: ${backgroundColor};
                z-index: -1;                
                display: none;
            }

            .bottom-spacer {
                height: 12rem;
            }

            .top-spacer {
                height: 7rem;
            }

            .controls {
                position: sticky;
                left: 0rem;
                z-index: 1;
                --action-switch-transition-duration: 0.2s;
                transition: opacity 0.5s;
            }

            .controls.show-inline {
                /* position: static; */
            }

            .controls.hide-controls {
                opacity: 0;
                transition-duration: 0s;
            }


            .separator {
                text-align: center;
                font-size: 15.5rem;
                letter-spacing: 0.5rem;
                color: ${color};
                font-weight: 700;
                border-top: 1rem solid ${color};
                border-bottom: 1rem solid ${color};
                margin: 10rem 18rem;
                padding: 12rem 0rem;
            }

            @media screen and (max-width: 1300px) and (max-height: 775px) {
                .separator {
                    font-size: 15rem;
                }
            }      
            @media screen and (max-width: ${mobileCutoff}px) {
                .controls {
                    position: static;
                }
            }
            `}</style>
        </>
    );
}